import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useApiClient } from '../utils/apiClient';
import { useBusinessSettings } from '../hooks/useBusinessSettings';
import { formatYYYYMMDDLocal, addDaysYYYYMMDD } from '../utils/dateOnly';

interface Customer {
  id: number;
  first_name: string;
  last_name: string;
  company_name?: string;
  display_name?: string;
  email: string;
  tax_exempt?: boolean;
}

interface Item {
  id: number;
  name: string;
  item_type: 'product' | 'service';
  description?: string;
  rate: number;
  effective_rate?: number;
  is_taxable: boolean;
}

interface InvoiceItem {
  id?: number;
  item_id?: number;
  description: string;
  qty: number;
  rate: number;
  amount: number;
  is_taxable: boolean;
}

interface Invoice {
  id?: number;
  customer_id: number;
  number?: string;
  issue_date: string;
  due_date: string;
  status: string;
  memo?: string;
  items: InvoiceItem[];
}

type NumericItemField = 'qty' | 'rate' | 'amount';
const NUMERIC_ITEM_FIELDS: NumericItemField[] = ['qty', 'rate', 'amount'];

export default function InvoiceFormPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { token, currentBusiness, isCookieMode, API_BASE_URL } = useAuth();
  const api = useApiClient();
  const { settings: businessSettings } = useBusinessSettings();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [itemNumericDrafts, setItemNumericDrafts] = useState<Record<string, string>>({});
  const [itemNumericErrors, setItemNumericErrors] = useState<Record<string, string>>({});
  
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [items, setItems] = useState<Item[]>([]);
  const [searchTerms, setSearchTerms] = useState<string[]>([]);
  const [filteredItemsList, setFilteredItemsList] = useState<Item[][]>([]);
  const [showAddItemModal, setShowAddItemModal] = useState(false);
  const [editingItemIndex, setEditingItemIndex] = useState<number>(-1);
  const [newItemData, setNewItemData] = useState({
    name: '',
    item_type: 'product' as 'product' | 'service',
    description: '',
    rate: 0,
    is_taxable: false
  });
  
  const [formData, setFormData] = useState<Invoice>({
    customer_id: 0,
    issue_date: formatYYYYMMDDLocal(businessSettings?.timezone),
    due_date: addDaysYYYYMMDD(formatYYYYMMDDLocal(businessSettings?.timezone), 30, businessSettings?.timezone),
    status: 'draft',
    memo: '',
    items: []
  });
  const selectedCustomer = customers.find((customer) => customer.id === formData.customer_id);
  const customerTaxExempt = selectedCustomer?.tax_exempt === true;

  // Check for customer_id in URL parameters
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const customerId = urlParams.get('customer_id');
    if (customerId && !id) {
      setFormData(prev => ({ ...prev, customer_id: parseInt(customerId) }));
    }
  }, [id]);

  const fetchCustomers = async () => {
    try {
      const data = await api.get<Customer[]>(`/customers`);
      setCustomers(data);
    } catch (err: any) {
      setError('Failed to fetch customers');
    }
  };

  const fetchItems = async (customerId?: number) => {
    try {
      const query = customerId ? `?customer_id=${encodeURIComponent(String(customerId))}` : '';
      const data = await api.get<Item[]>(`/items${query}`);
      setItems(data);
      } catch (err: any) {
      setError('Failed to fetch items');
    }
  };

  const fetchInvoice = async () => {
    if (!id) return;
    
    setLoading(true);
    try {
      const invoice = await api.get<any>(`/invoices/${id}`);
      const normalizeDate = (d: any) => {
        if (!d) return '';
        if (typeof d === 'string') return d.includes('T') ? d.split('T')[0] : d;
        if (d instanceof Date) return d.toISOString().split('T')[0];
        return '';
      };
      setFormData({
        customer_id: invoice.customer_id,
        number: invoice.number,
        issue_date: normalizeDate(invoice.issue_date),
        due_date: normalizeDate(invoice.due_date),
        status: invoice.status,
        memo: invoice.terms || '',
        items: invoice.items || []
      });
    } catch (err: any) {
      setError('Failed to fetch invoice');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (currentBusiness) {
      fetchCustomers();
      if (id) {
        fetchInvoice();
      }
    }
  }, [currentBusiness, id, token]);

  useEffect(() => {
    if (!currentBusiness) return;
    fetchItems(formData.customer_id || undefined);
  }, [currentBusiness, formData.customer_id]);

  useEffect(() => {
    if (!customerTaxExempt) return;
    if (!formData.items.some((item) => item.is_taxable)) return;
    setFormData((prev) => ({
      ...prev,
      items: prev.items.map((item) => ({ ...item, is_taxable: false })),
    }));
  }, [customerTaxExempt, formData.items]);

  useEffect(() => {
    setFilteredItemsList(searchTerms.map((term) => {
      if (!term) return items;
      return items.filter(item =>
        item.name.toLowerCase().includes(term.toLowerCase()) ||
        (item.description && item.description.toLowerCase().includes(term.toLowerCase()))
      );
    }));
  }, [searchTerms, items]);

  const handleFormChange = (field: keyof Invoice, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const getNumericItemFieldKey = (index: number, field: NumericItemField) => `${index}:${field}`;

  const getNumericItemFieldDisplayValue = (index: number, field: NumericItemField) => {
    const key = getNumericItemFieldKey(index, field);
    if (Object.prototype.hasOwnProperty.call(itemNumericDrafts, key)) {
      return itemNumericDrafts[key];
    }
    const value = formData.items[index]?.[field];
    if (value === null || value === undefined) return '';
    const numericValue = Number(value);
    return Number.isFinite(numericValue) ? String(value) : '';
  };

  const parseNumericItemDraft = (rawValue: string) => {
    const trimmed = rawValue.trim();
    if (trimmed === '') {
      return { isValid: false, message: 'Required' };
    }
    const parsedValue = Number(trimmed);
    if (!Number.isFinite(parsedValue) || parsedValue < 0) {
      return { isValid: false, message: 'Enter a valid non-negative number' };
    }
    return { isValid: true, parsedValue };
  };

  const handleNumericItemDraftChange = (index: number, field: NumericItemField, rawValue: string) => {
    const key = getNumericItemFieldKey(index, field);
    setItemNumericDrafts(prev => ({ ...prev, [key]: rawValue }));
    setItemNumericErrors(prev => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const handleNumericItemFieldBlur = (index: number, field: NumericItemField) => {
    const key = getNumericItemFieldKey(index, field);
    if (!Object.prototype.hasOwnProperty.call(itemNumericDrafts, key)) return;

    const result = parseNumericItemDraft(itemNumericDrafts[key] || '');
    if (!result.isValid) {
      setItemNumericErrors(prev => ({ ...prev, [key]: result.message || 'Invalid value' }));
      return;
    }

    updateItem(index, field, result.parsedValue);
    setItemNumericDrafts(prev => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
    setItemNumericErrors(prev => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const validateAndNormalizeNumericItems = () => {
    const nextErrors: Record<string, string> = {};
    const normalizedItems = formData.items.map((item, index) => {
      const nextItem = { ...item };
      for (const field of NUMERIC_ITEM_FIELDS) {
        const key = getNumericItemFieldKey(index, field);
        if (Object.prototype.hasOwnProperty.call(itemNumericDrafts, key)) {
          const draftResult = parseNumericItemDraft(itemNumericDrafts[key] || '');
          if (!draftResult.isValid) {
            nextErrors[key] = draftResult.message || 'Invalid value';
            continue;
          }
          nextItem[field] = draftResult.parsedValue as number;
        }
        const finalValue = Number(nextItem[field]);
        if (!Number.isFinite(finalValue) || finalValue < 0) {
          nextErrors[key] = 'Required';
        }
      }
      return nextItem;
    });

    setItemNumericErrors(nextErrors);

    const firstInvalidKey = Object.keys(nextErrors)[0];
    if (firstInvalidKey) {
      setTimeout(() => {
        const input = document.querySelector<HTMLInputElement>(`[data-item-field-key="${firstInvalidKey}"]`);
        input?.focus();
      }, 0);
      return { isValid: false, normalizedItems };
    }

    setItemNumericDrafts({});
    return { isValid: true, normalizedItems };
  };

  const addItem = () => {
    setFormData(prev => ({
      ...prev,
      items: [...prev.items, {
        description: '',
        qty: 1,
        rate: 0,
        amount: 0,
        is_taxable: false
      }]
    }));
    setSearchTerms(prev => [...prev, '']);
    setFilteredItemsList(prev => [...prev, items]);
  };

  const updateItem = (index: number, field: keyof InvoiceItem, value: any) => {
    setFormData(prev => {
      const newItems = [...prev.items];
      newItems[index] = { ...newItems[index], [field]: field === 'is_taxable' && customerTaxExempt ? false : value };
      
      // Auto-calculate amount if qty or rate changed
      if (field === 'qty' || field === 'rate') {
        const item = newItems[index];
        const rate = Number(item.rate) || 0;
        const qty = Number(item.qty) || 0;
        newItems[index] = { ...item, amount: qty * rate };
      }
      
      // Auto-calculate rate if amount changed manually
      if (field === 'amount') {
        const item = newItems[index];
        if (item.qty > 0) {
          newItems[index] = { ...item, rate: value / item.qty };
        }
      }
      
      return { ...prev, items: newItems };
    });
  };

  const removeItem = (index: number) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
    setItemNumericDrafts({});
    setItemNumericErrors({});
  };

  const selectItem = (index: number, item: Item) => {
    const rate = Number(item.effective_rate ?? item.rate) || 0;
    updateItem(index, 'item_id', item.id);
    // Keep the description field as is, don't overwrite it with the item name
    // The item name will be displayed separately
    updateItem(index, 'rate', rate);
    updateItem(index, 'is_taxable', customerTaxExempt ? false : item.is_taxable);
    updateItem(index, 'amount', rate);
    setSearchTerms(prev => {
      const newTerms = [...prev];
      newTerms[index] = ''; // Clear search term for this line item
      return newTerms;
    });
  };

  const handleAddNewItem = async () => {
    setSaving(true);
    try {
      const newItem = await api.post<any>(`/items`, newItemData);
      await fetchItems(formData.customer_id || undefined);
      // Update the line item that triggered the "Add New Item" action
      if (editingItemIndex !== -1) {
        // Update the line item with the new item's details
        const rate = Number(newItem.rate) || 0;
        updateItem(editingItemIndex, 'item_id', newItem.id);
        // Set description to the item's description if available, otherwise use the search term
        const description = newItem.description || searchTerms[editingItemIndex];
        updateItem(editingItemIndex, 'description', description);
        updateItem(editingItemIndex, 'rate', rate);
        updateItem(editingItemIndex, 'is_taxable', customerTaxExempt ? false : newItem.is_taxable);
        updateItem(editingItemIndex, 'amount', rate);
      }
      
      setShowAddItemModal(false);
      setEditingItemIndex(-1);
      setNewItemData({
        name: '',
        item_type: 'product',
        description: '',
        rate: 0,
        is_taxable: false
      });
    } catch (err: any) {
      setError('Failed to create item');
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.customer_id) {
      setError('Please select a customer');
      return;
    }

    const normalizedResult = validateAndNormalizeNumericItems();
    if (!normalizedResult.isValid) {
      setError('Please complete required numeric fields for all line items');
      return;
    }

    const normalizedFormData = {
      ...formData,
      items: normalizedResult.normalizedItems.map((item) => ({
        ...item,
        is_taxable: customerTaxExempt ? false : item.is_taxable,
      }))
    };
    setFormData(normalizedFormData);

    setSaving(true);
    setError('');
    try {
      const data = {
        ...normalizedFormData,
        terms: normalizedFormData.memo // Backend expects 'terms' field
      };
      
      if (id) {
        await api.put<any>(`/invoices/${id}`, data);
        // After editing an invoice, go to the associated customer's detail page
        const customerId = data.customer_id || formData.customer_id;
        if (customerId) {
          navigate(`/customers/${customerId}`);
        } else {
          navigate('/invoices');
        }
      } else {
        await api.post<any>(`/invoices`, data);
        // For creation, retain existing behavior (back to invoices list)
        navigate('/invoices');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to save invoice');
    } finally {
      setSaving(false);
    }
  };

  const getCustomerDisplayName = (customer: Customer) => {
    if (customer.display_name) return customer.display_name;
    if (customer.company_name) return customer.company_name;
    return `${customer.first_name} ${customer.last_name}`;
  };

  const totalAmount = formData.items.reduce((sum, item) => sum + (Number(item.amount) || 0), 0);

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-3xl font-bold text-gray-900 mb-6">
          {id ? 'Edit Invoice' : 'Create Invoice'}
        </h1>

        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
            <p className="text-red-800">{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Customer
              </label>
              <select
                value={formData.customer_id}
                onChange={(e) => handleFormChange('customer_id', parseInt(e.target.value))}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              >
                <option value="">Select a customer</option>
                {customers.map(customer => (
                  <option key={customer.id} value={customer.id}>
                    {getCustomerDisplayName(customer)}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Invoice Number
              </label>
              <input
                type="text"
                value={formData.number || ''}
                onChange={(e) => handleFormChange('number', e.target.value)}
                placeholder="Auto-generated if left empty"
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Issue Date
              </label>
              <input
                type="date"
                value={formData.issue_date}
                onChange={(e) => {
                  handleFormChange('issue_date', e.target.value);
                  // Auto-set due date to 30 days from issue date
                  if (e.target.value) {
                    handleFormChange('due_date', addDaysYYYYMMDD(e.target.value, 30, businessSettings?.timezone));
                  }
                }}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Due Date
              </label>
              <input
                type="date"
                value={formData.due_date}
                onChange={(e) => handleFormChange('due_date', e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>


          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Memo
            </label>
            <textarea
              value={formData.memo}
              onChange={(e) => handleFormChange('memo', e.target.value)}
              rows={3}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Optional memo or notes"
            />
          </div>

          <div>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-gray-900">Line Items</h2>
              <button
                type="button"
                onClick={addItem}
                className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
              >
                Add Item
              </button>
            </div>

            {formData.items.map((item, index) => (
              <div
                key={index}
                className={`border border-gray-200 rounded-lg p-4 mb-4 ${index % 2 === 0 ? 'bg-gray-50' : 'bg-gray-200'}`}
              >
                <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Product or Service
                    </label>
                    
                    {/* Show selected item name if an item is selected */}
                    {item.item_id && (
                      <div className="mb-2 p-2 bg-blue-50 border border-blue-200 rounded-md">
                        <div className="font-medium text-blue-900">
                          {items.find(i => i.id === item.item_id)?.name || 'Unknown Item'}
                        </div>
                        <div className="text-sm text-blue-700">
                          ${(Number(item.rate) || 0).toFixed(2)} • {items.find(i => i.id === item.item_id)?.item_type || 'product'}
                        </div>
                      </div>
                    )}
                    
                    {/* Search field for selecting items */}
                    <div className="relative">
                      <input
                        type="text"
                        value={searchTerms[index] || ''}
                        onChange={(e) => {
                          const newTerms = [...searchTerms];
                          newTerms[index] = e.target.value;
                          setSearchTerms(newTerms);
                        }}
                        placeholder={item.item_id ? "Search for different item..." : "Search items or enter description"}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      {searchTerms[index] && (
                        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
                          {filteredItemsList[index]?.map(item => (
                            <div
                              key={item.id}
                              className="px-3 py-2 hover:bg-gray-100 cursor-pointer"
                              onClick={() => selectItem(index, item)}
                            >
                              <div className="font-medium">{item.name}</div>
                              <div className="text-sm text-gray-500">
                                ${(Number(item.rate) || 0).toFixed(2)} • {item.item_type}
                              </div>
                            </div>
                          ))}
                          {filteredItemsList[index]?.length === 0 && (
                            <div className="px-3 py-2">
                              <button
                                type="button"
                                onClick={() => {
                                  setNewItemData(prev => ({ ...prev, name: searchTerms[index] }));
                                  setEditingItemIndex(index);
                                  setShowAddItemModal(true);
                                }}
                                className="text-blue-600 hover:text-blue-800 font-medium"
                              >
                                Add New Item
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    
                    {/* Description field */}
                    <div className="mt-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Description
                      </label>
                      <textarea
                        value={item.description}
                        onChange={(e) => updateItem(index, 'description', e.target.value)}
                        placeholder="Additional details about this line item..."
                        rows={2}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Qty
                    </label>
                    <input
                      type="number"
                      value={getNumericItemFieldDisplayValue(index, 'qty')}
                      onChange={(e) => handleNumericItemDraftChange(index, 'qty', e.target.value)}
                      onBlur={() => handleNumericItemFieldBlur(index, 'qty')}
                      data-item-field-key={getNumericItemFieldKey(index, 'qty')}
                      min="0"
                      step="0.01"
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    {itemNumericErrors[getNumericItemFieldKey(index, 'qty')] && (
                      <p className="mt-1 text-xs text-red-600">{itemNumericErrors[getNumericItemFieldKey(index, 'qty')]}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Rate
                    </label>
                    <input
                      type="number"
                      value={getNumericItemFieldDisplayValue(index, 'rate')}
                      onChange={(e) => handleNumericItemDraftChange(index, 'rate', e.target.value)}
                      onBlur={() => handleNumericItemFieldBlur(index, 'rate')}
                      data-item-field-key={getNumericItemFieldKey(index, 'rate')}
                      min="0"
                      step="0.01"
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    {itemNumericErrors[getNumericItemFieldKey(index, 'rate')] && (
                      <p className="mt-1 text-xs text-red-600">{itemNumericErrors[getNumericItemFieldKey(index, 'rate')]}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Amount
                    </label>
                    <input
                      type="number"
                      value={getNumericItemFieldDisplayValue(index, 'amount')}
                      onChange={(e) => handleNumericItemDraftChange(index, 'amount', e.target.value)}
                      onBlur={() => handleNumericItemFieldBlur(index, 'amount')}
                      data-item-field-key={getNumericItemFieldKey(index, 'amount')}
                      min="0"
                      step="0.01"
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    {itemNumericErrors[getNumericItemFieldKey(index, 'amount')] && (
                      <p className="mt-1 text-xs text-red-600">{itemNumericErrors[getNumericItemFieldKey(index, 'amount')]}</p>
                    )}
                  </div>

                  <div className="flex items-end space-x-2">
                    <div
                      className={`flex items-center ${customerTaxExempt ? 'cursor-not-allowed opacity-60' : ''}`}
                      title={customerTaxExempt ? 'Client is tax-exempt.' : undefined}
                    >
                      <input
                        type="checkbox"
                        checked={customerTaxExempt ? false : item.is_taxable}
                        onChange={(e) => updateItem(index, 'is_taxable', e.target.checked)}
                        disabled={customerTaxExempt}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label className="ml-2 text-sm text-gray-700">Tax</label>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeItem(index)}
                      className="text-red-600 hover:text-red-800"
                    >
                      Remove
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="flex justify-between items-center pt-6 border-t border-gray-200">
            <div className="text-xl font-semibold">
              Total: ${totalAmount.toFixed(2)}
            </div>
            <div className="space-x-3">
              <button
                type="button"
                onClick={() => navigate('/invoices')}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                {saving ? 'Saving...' : (id ? 'Update' : 'Create')} Invoice
              </button>
            </div>
          </div>
        </form>
      </div>

      {/* Add New Item Modal */}
      {showAddItemModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-bold mb-4">Add New Item</h2>
            <form onSubmit={(e) => { e.preventDefault(); handleAddNewItem(); }} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Name</label>
                <input
                  type="text"
                  value={newItemData.name}
                  onChange={(e) => setNewItemData(prev => ({ ...prev, name: e.target.value }))}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">Item Type</label>
                <select
                  value={newItemData.item_type}
                  onChange={(e) => setNewItemData(prev => ({ ...prev, item_type: e.target.value as 'product' | 'service' }))}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="product">Product</option>
                  <option value="service">Service</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">Description</label>
                <textarea
                  value={newItemData.description}
                  onChange={(e) => setNewItemData(prev => ({ ...prev, description: e.target.value }))}
                  rows={3}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">Rate/Price</label>
                <input
                  type="number"
                  value={newItemData.rate}
                  onChange={(e) => setNewItemData(prev => ({ ...prev, rate: parseFloat(e.target.value) || 0 }))}
                  step="0.01"
                  min="0"
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              
              <div className="flex items-center">
                <input
                  type="checkbox"
                  checked={newItemData.is_taxable}
                  onChange={(e) => setNewItemData(prev => ({ ...prev, is_taxable: e.target.checked }))}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label className="ml-2 block text-sm text-gray-900">
                  Taxable Item
                </label>
              </div>
              
              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddItemModal(false);
                    setEditingItemIndex(-1);
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                >
                  {saving ? 'Creating...' : 'Create Item'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}; 